import { useState } from 'react';
import { useSelector } from 'react-redux';
import { useMap } from '@vis.gl/react-google-maps';
import * as R from 'ramda';

import { Snackbar } from '@atom/mui';
import { MapSearch } from '@atom/types/map';
import api from '@atom/utilities/api';
import { USERS_ENDPOINT } from '@atom/utilities/endpoints';

import usePlacesAutocomplete from '../hooks/usePlacesAutocomplete';
import { getCurrentUserLocation, getGeoJSONLocation } from '../mapUtilities';

const SEARCH_ZOOM = 15;

type UseMapSearchArgs = {
  initialSearchTerm?: string;
  onSearchChange?: (search: MapSearch) => void;
};

export type MapSearchState = {
  term: string;
  suggestions: google.maps.places.AutocompleteSuggestion[];
  handleInputChange: (event: unknown, value: string, reason: string) => void;
  handleSelect: (
    event: unknown,
    value: string | google.maps.places.AutocompleteSuggestion | null,
  ) => Promise<void>;
  handleClear: () => void;
  handleCurrentLocation: () => Promise<void>;
};

const useMapSearch = ({
  initialSearchTerm,
  onSearchChange,
}: UseMapSearchArgs): MapSearchState => {
  const map = useMap();
  const userId = useSelector(R.pathOr('', ['userProfile', 'userId']));

  const { suggestions, fetchSuggestions, clearSuggestions, resolveSelection } =
    usePlacesAutocomplete();

  const [term, setTerm] = useState(initialSearchTerm ?? '');

  const recenter = (
    location: google.maps.LatLngLiteral,
    searchTerm?: string,
  ) => {
    map?.panTo(location);
    map?.setZoom(SEARCH_ZOOM);
    onSearchChange?.({ searchPoint: location, searchTerm });
  };

  const handleInputChange = (
    _event: unknown,
    value: string,
    reason: string,
  ) => {
    if (reason === 'reset') {
      return;
    }

    setTerm(value);
    fetchSuggestions(value);
  };

  const handleSelect = async (
    _event: unknown,
    value: string | google.maps.places.AutocompleteSuggestion | null,
  ) => {
    const resolution = await resolveSelection(term, value);

    if (!resolution) {
      return;
    }

    recenter(resolution.location, resolution.formattedAddress);

    if (!resolution.isCoordinate) {
      setTerm(resolution.formattedAddress ?? '');
    }
  };

  const handleClear = () => {
    setTerm('');
    clearSuggestions();
    onSearchChange?.({ searchPoint: undefined, searchTerm: undefined });
  };

  const handleCurrentLocation = async () => {
    const location = await getCurrentUserLocation();

    if (location) {
      recenter(location);

      if (userId) {
        api.patch(`${USERS_ENDPOINT}/${userId}`, {
          location: getGeoJSONLocation(location),
        });
      }
    } else {
      Snackbar.error({
        message:
          'Atom cannot retrieve your location. Please check your browser permission settings.',
      });
    }
  };

  return {
    term,
    suggestions,
    handleInputChange,
    handleSelect,
    handleClear,
    handleCurrentLocation,
  };
};

export default useMapSearch;
